import React, { FC, useCallback, useEffect, useState } from "react";

import {
    Button,
    EmailInput,
    LoadingSpinner,
    OnboardingAside,
    OnboardingContent,
    SmartDropdown,
    SmartDropdownOption,
    validateEmail,
} from "@xapp/components";

import { ClientApp } from "models/App";

export interface SendLeadsStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;

    aside?: React.ReactNode;

    app?: Pick<ClientApp, "appId" | "leadsContact" | "beta">;
    /**
     * Callback when user clicks continue
     */
    onNext?: (app: Pick<ClientApp, "appId" | "leadsContact" | "beta">) => void;
    /**
     * Callback when user clicks back
     */
    onBack?: () => void;
    /**
     * Saving state to show loading spinner
     */
    saving?: boolean;
    /**
     * Loading state while loading the data
     */
    loading?: boolean;
}

export const SendLeadsStep: FC<SendLeadsStepProps> = (props) => {
    const { aside, app, onNext, onBack, loading, saving } = props;

    const [leadsContact, setLeadsContact] = useState<string>(app?.leadsContact || "");
    const [leadsContactValid, setLeadsContactValid] = useState<boolean>(false);
    const [selectedIntegration, setSelectedIntegration] = useState<string[]>([]);

    useEffect(() => {
        if (app?.beta?.onboarding?.leadsIntegration) {
            setSelectedIntegration(app.beta.onboarding.leadsIntegration);
        }
        if (app?.leadsContact) {
            setLeadsContact(app.leadsContact);
            setLeadsContactValid(validateEmail(app.leadsContact));
        }
    }, [app]);

    const fsmOptions: SmartDropdownOption[] = [
        {
            id: "none",
            name: "I don't use one",
        },
        {
            id: "fieldpulse",
            name: "FieldPulse",
        },
        {
            id: "housecallpro",
            name: "HousecallPro",
        },
        {
            id: "jobber",
            name: "Jobber",
        },
        {
            id: "surefire",
            name: "Surefire Local",
        },
        {
            id: "servicetitan",
            name: "ServiceTitan",
        },
        {
            id: "servicefusion",
            name: "Service Fusion",
        },
        {
            id: "workiz",
            name: "Workiz",
        },
        {
            id: "lacrm",
            name: "Less Annoying CRM",
        },
        {
            id: "salesforce",
            name: "Salesforce",
        },
        {
            id: "hubspot",
            name: "HubSpot",
        },
        {
            id: "other",
            name: "Other / Not Listed",
        },
    ];

    const handleFSMChange = useCallback((selectedOption: SmartDropdownOption) => {
        setSelectedIntegration([selectedOption.name]);
    }, []);

    const handleLeadsContactUpdated = useCallback((value: string, valid: boolean) => {
        setLeadsContact(value);
        setLeadsContactValid(valid);
    }, []);

    const handleContinue = useCallback(() => {
        onNext?.({
            appId: app?.appId,
            leadsContact,
            beta: {
                ...app?.beta,
                onboarding: {
                    ...app?.beta?.onboarding,
                    leadsIntegration: selectedIntegration.length > 0 ? selectedIntegration : undefined,
                },
            },
        });
    }, [onNext, app?.appId, app?.beta, leadsContact, selectedIntegration]);

    const canProceed = leadsContact && leadsContactValid;

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}

            <OnboardingContent>
                {loading || saving ? (
                    <div
                        style={{
                            padding: "40px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "400px",
                        }}
                    >
                        <LoadingSpinner
                            textStyle={{ fontSize: "1em" }}
                            loadingText={
                                saving ? "Saving lead destination settings..." : "Loading lead destination settings..."
                            }
                        />
                    </div>
                ) : (
                    <div style={{ padding: "20px", maxWidth: "600px", margin: "0 auto" }}>
                        <div style={{ marginBottom: "30px" }}>
                            <h3 style={{ marginBottom: "10px" }}>Email Address</h3>
                            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
                                You can access your leads anytime in Studio but we also need an email address to send
                                them to so you can get them immediately.
                            </p>

                            <EmailInput
                                variation="outlined"
                                label="Leads Email"
                                value={leadsContact}
                                onChange={handleLeadsContactUpdated}
                            />
                        </div>

                        <div style={{ marginBottom: "30px" }}>
                            <h3 style={{ marginBottom: "10px" }}>Field Service Management (FSM)</h3>
                            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
                                We can also send your leads directly to your FSM/CRM.
                            </p>

                            <SmartDropdown
                                slugLabel={selectedIntegration[0] || "Select FSM/CRM"}
                                options={fsmOptions}
                                onChange={handleFSMChange}
                            />
                            <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
                                We will help you set this up later.
                            </small>
                        </div>

                        {/* Action Buttons */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: "40px",
                            }}
                        >
                            {onBack && <Button label="Back" variation="secondary" onClick={onBack} />}
                            <Button
                                label="Continue"
                                disabled={!canProceed}
                                onClick={handleContinue}
                                style={{ marginLeft: "auto" }}
                            />
                        </div>
                    </div>
                )}
            </OnboardingContent>
        </>
    );
};
